import Vue from 'vue';
import UtilDateTime from '@/utils/UtilDateTime';
import useColaboradorStore from '../stores/ColaboradorStore';

const extractList = (response) => {
    const data = response?.data?.data?.data ?? response?.data?.data ?? response?.data;
    return Array.isArray(data) ? data : [];
};

const getId = (value) => value?.id || value?._id || value || undefined;
const toNumber = (value) => Number(value) || 0;
const cleanBalance = (balance) => {
    const payload = { ...balance };
    ['createAt', 'updateAt', 'updatedAt', 'createBy', 'updateBy', 'deleteAt', 'deleteBy', 'isDeleted'].forEach((field) => {
        delete payload[field];
    });
    payload.item = getId(payload.item);
    payload.localEstoque = getId(payload.localEstoque);
    return payload;
};

const assertSuccess = (response, message) => {
    if (!response || response.status < 200 || response.status >= 300 || response?.data?.errors) {
        throw new Error(response?.data?.message || message);
    }
};

const getStockBalances = async (itemCodigo) => {
    const response = await Vue.prototype.$http.get('/Entity/StockBalance/search-fields', {
        params: {
            searchFields: 'itemCodigo',
            value: itemCodigo,
            mode: 'Equals',
            limit: 999,
            sortField: 'createAt'
        }
    });

    assertSuccess(response, `Não foi possível consultar o saldo do item ${itemCodigo}.`);
    return extractList(response).filter((balance) => String(balance?.itemCodigo) === String(itemCodigo));
};

const getExistingMovements = async (description) => {
    const response = await Vue.prototype.$http.get('/Entity/HistoricoMovimentacaoItem/search-fields', {
        params: {
            searchFields: 'descricao',
            value: description,
            mode: 'Equals',
            limit: 999
        }
    });

    assertSuccess(response, 'Não foi possível verificar o histórico da saída fiscal.');
    return extractList(response);
};

const getServiceOrderItem = async (serviceOrderItemId) => {
    if (!serviceOrderItemId) return null;

    const response = await Vue.prototype.$http.get(`/Entity/OrdemDeServicoItens/${serviceOrderItemId}`);
    assertSuccess(response, `Não foi possível consultar o item da OS ${serviceOrderItemId}.`);
    return response?.data?.data || null;
};

const createMovementPlan = async (items, existingMovements) => {
    const quantitiesByCode = items.reduce((result, item) => {
        const itemCodigo = String(item?.itemCodigo || '').trim();
        const quantidade = toNumber(item?.quantidade);

        if (!itemCodigo) throw new Error('Há item da nota fiscal sem código para movimentação de estoque.');
        if (quantidade <= 0) throw new Error(`A quantidade do item ${itemCodigo} deve ser maior que zero.`);

        const current = result.get(itemCodigo) || { quantity: 0, sourceItems: [] };
        current.quantity += quantidade;
        current.sourceItems.push(item);
        result.set(itemCodigo, current);
        return result;
    }, new Map());

    const movedByItem = existingMovements.reduce((result, movement) => {
        const itemId = getId(movement?.item);
        if (itemId) result.set(String(itemId), (result.get(String(itemId)) || 0) + toNumber(movement?.quantidade));
        return result;
    }, new Map());

    const plan = [];

    for (const [itemCodigo, itemGroup] of quantitiesByCode) {
        const balances = await getStockBalances(itemCodigo);
        if (!balances.length) throw new Error(`Saldo de estoque não encontrado para o item ${itemCodigo}.`);

        const stockItemId = getId(balances[0]?.item);
        let remainingQuantity = Math.max(0, itemGroup.quantity - (movedByItem.get(String(stockItemId)) || 0));
        const sourceItem = itemGroup.sourceItems[0] || {};
        const serviceOrderItemId = getId(sourceItem?.ordemDeServicoItem);
        const serviceOrderItem = await getServiceOrderItem(serviceOrderItemId);

        for (const balance of balances) {
            if (remainingQuantity <= 0) break;

            const reservedQuantity = toNumber(balance?.quantidadeReservado);
            const quantity = Math.min(reservedQuantity, remainingQuantity);
            if (quantity <= 0) continue;

            plan.push({ balance, quantity, sourceItem, serviceOrderItem });
            remainingQuantity -= quantity;
        }

        if (remainingQuantity > 0) {
            throw new Error(`Reserva insuficiente para o item ${itemCodigo}. Faltam ${remainingQuantity} unidade(s).`);
        }
    }

    return plan;
};

const realizarSaida = async ({ notaFiscalEmitida, numeroNotaFiscal, itens }) => {
    if (!notaFiscalEmitida) throw new Error('Nota fiscal emitida não informada para a saída de estoque.');
    if (!Array.isArray(itens) || !itens.length) return;

    const description = `SAÍDA DE ESTOQUE DA NOTA FISCAL N°${numeroNotaFiscal || notaFiscalEmitida} [${notaFiscalEmitida}]`;
    const existingMovements = await getExistingMovements(description);
    const plan = await createMovementPlan(itens, existingMovements);
    const colaboradorStore = useColaboradorStore();

    for (const { balance, quantity, sourceItem, serviceOrderItem } of plan) {
        const balanceId = getId(balance);
        const itemId = getId(balance?.item);
        const now = UtilDateTime.nowUTC();
        const updateResponse = await Vue.prototype.$http.put('/Entity/StockBalance', {
            ...cleanBalance(balance),
            id: balanceId,
            quantidadeReservado: toNumber(balance?.quantidadeReservado) - quantity,
            quantidadeSaida: toNumber(balance?.quantidadeSaida) + quantity,
            dataAlteracao: now
        });
        assertSuccess(updateResponse, `Não foi possível atualizar o saldo do item ${balance?.itemCodigo}.`);

        const codeResponse = await Vue.prototype.$http.get(
            '/Entity/AutoInc?schemaName=HistoricoMovimentacaoItem&field=codigo&mask=######'
        );
        assertSuccess(codeResponse, 'Não foi possível gerar o código do histórico de estoque.');

        const historyResponse = await Vue.prototype.$http.post('/Entity/HistoricoMovimentacaoItem', {
            codigo: codeResponse.data,
            tipo: 'Saída',
            descricao: description,
            dataMovimentacao: now,
            quantidade: quantity,
            colaboradorDaMovimentacao: colaboradorStore?.colaborador?.id || undefined,
            saldoDeEstoque: balanceId,
            item: itemId,
            notaFiscalEmitida,
            os: getId(serviceOrderItem?.ordemDeServico || sourceItem?.ordemDeServico),
            osItem: getId(serviceOrderItem) || getId(sourceItem?.ordemDeServicoItem),
            job: getId(serviceOrderItem?.job || serviceOrderItem?.ordemDeServico?.job),
            ICMSValor: balance?.ICMSValor,
            PISValor: balance?.PISValor,
            COFINSValor: balance?.COFINSValor,
            IBSValor: balance?.IBSValor,
            CBSValor: balance?.CBSValor
        });
        assertSuccess(historyResponse, `Não foi possível criar o histórico do item ${balance?.itemCodigo}.`);
    }
};

export default {
    realizarSaida
};
